import QRCode from "react-qr-code";
import { useGetUserInfoQuery } from "../../../app/rootReducer";
import { useGetAuthSettingsQuery } from "../../../pages/accountPage/AccountSlice";
import { JSX, useEffect, useState } from "react";
import { SettingsAuthenticationInput } from "./SettingsAuthenticationInput";
import { SettingsSendCode } from "./SettingsSendCode";
import { TwoFactorTypeEnum } from "../../../common/enums/TwoFactorTypeEnum";
import { useStringTranslation } from "../../../common/hooks/useStringTranslation";

export const SettingsTab = (): JSX.Element => {
    const userData = useGetUserInfoQuery();
    const { activeLanguage, phoneNumber } = userData?.data || {
        activeLanguage: null,
    };

    const { t, isLoading: isTranslationsLoading } = useStringTranslation();
    const { data: authSettings, isLoading: isLoadingAuthSettings } =
        useGetAuthSettingsQuery();

    const [selectedNewMethod, setSelectedNewMethod] = useState<string | null>(
        null,
    );
    const [authCodeInput, setAuthCodeInput] = useState<string>("");
    const [authMethodIsSet, setAuthMethodIsSet] = useState<boolean>(false);
    const [authCodeIsSent, setAuthCodeIsSent] = useState<boolean>(false);

    const [phoneNumberInput, setPhoneNumberInput] = useState<string>();

    const { authentication_methods, current_authentication_method } =
        authSettings || {
            authentication_methods: [],
            current_authentication_method: "",
        };

    const translationsLoaded = !isTranslationsLoading && !!activeLanguage;
    const selectedMethod = authentication_methods.find(
        (method) => method.authentication_method === selectedNewMethod,
    );

    useEffect(() => {
        setPhoneNumberInput(phoneNumber);
    }, [phoneNumber]);

    return (
        <>
            <div className="c-account-content c-account-content--settings">
                <div className="c-account-content__header">
                    {translationsLoaded ? (
                        <h1>{t("settings")}</h1>
                    ) : (
                        <h1 className="skeleton skeleton-title"></h1>
                    )}
                </div>

                {!authSettings && !isLoadingAuthSettings && (
                    <div className="c-account-content__settings-content">
                        <p>{t("2fa-is-disabled")}</p>
                    </div>
                )}

                {!!authSettings && (
                    <div className="c-account-content__settings-content">
                        <div className="c-account-content__settings-container">
                            <div className="c-account-content__column">
                                {translationsLoaded ? (
                                    <>
                                        {!!current_authentication_method && (
                                            <div className="c-account-content__column__inline">
                                                <span className="c-account-content__column__inline--label">
                                                    {t("active-2fa-method")}:
                                                </span>
                                                <span>
                                                    {
                                                        current_authentication_method
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span className="c-account-content__column__text c-account-content__column__text--label skeleton skeleton-name"></span>
                                )}
                            </div>
                        </div>

                        {!!authentication_methods.length && (
                            <div className="c-account-content__settings-container">
                                <h3>{t("choose-another-2fa-method")}:</h3>

                                <div className="c-account-content__button-container">
                                    {authentication_methods.map((method) => (
                                        <button
                                            key={method.label}
                                            className="c-btn c-btn--primary"
                                            onClick={() =>
                                                setSelectedNewMethod(
                                                    method.authentication_method,
                                                )
                                            }
                                            disabled={
                                                method.authentication_method ===
                                                current_authentication_method
                                            }
                                        >
                                            <span>{method.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!!selectedMethod && (
                <div className="c-account-content c-account-content--settings-2fa">
                    <div className="c-account-content__header">
                        {translationsLoaded ? (
                            <h1>
                                {t("set-the-method").replace(
                                    "%method%",
                                    selectedMethod.label,
                                )}
                            </h1>
                        ) : (
                            <h1 className="skeleton skeleton-title"></h1>
                        )}
                    </div>

                    {selectedMethod.authentication_method ===
                        TwoFactorTypeEnum.authenticator && (
                        <>
                            <div className="c-account-content__settings-container">
                                {translationsLoaded ? (
                                    <>
                                        <h2>
                                            {t("step")} 1:
                                            <br />
                                            {t("download-app")}
                                        </h2>
                                        <p>
                                            {t("download-an-authenticator-app")}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="skeleton skeleton-title"></h2>
                                        <p className="skeleton skeleton-text"></p>
                                    </>
                                )}
                            </div>

                            <div className="c-account-content__settings-container c-account-content__settings-container--row">
                                <div className="c-account-content__settings-container__text">
                                    {translationsLoaded ? (
                                        <>
                                            <h2>
                                                {t("step")} 2:
                                                <br />
                                                {t("scan-the-qr-code")}
                                            </h2>
                                            <p>
                                                {t(
                                                    "scan-the-qr-code-with-the-authenticator-app",
                                                )}
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <h2 className="skeleton skeleton-title"></h2>
                                            <p className="skeleton skeleton-text"></p>
                                        </>
                                    )}
                                </div>

                                <QRCode value={selectedMethod?.url ?? ""} />
                            </div>

                            <div className="c-account-content__settings-container">
                                {translationsLoaded ? (
                                    <>
                                        <h2>
                                            {t("step")} 3:
                                            <br />
                                            {t("enter-the-code")}
                                        </h2>
                                        <p>
                                            {t(
                                                "enter-the-verification-code-you-receive-from-the-authenticator-app",
                                            )}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="skeleton skeleton-title"></h2>
                                        <p className="skeleton skeleton-text"></p>
                                    </>
                                )}

                                <SettingsAuthenticationInput
                                    authCodeInput={authCodeInput}
                                    authMethodIsSet={authMethodIsSet}
                                    selectedNewMethod={selectedNewMethod}
                                    setAuthCodeInput={setAuthCodeInput}
                                    setAuthMethodIsSet={setAuthMethodIsSet}
                                />
                            </div>
                        </>
                    )}

                    {selectedMethod.authentication_method ===
                        TwoFactorTypeEnum.email && (
                        <>
                            <div className="c-account-content__settings-container">
                                {translationsLoaded ? (
                                    <>
                                        <h2>
                                            {t("step")} 1:
                                            <br />
                                            {t(
                                                "receive-your-verification-code",
                                            )}
                                        </h2>
                                        <p>
                                            {t(
                                                "receive-your-verification-code-by-email",
                                            )}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="skeleton skeleton-title"></h2>
                                        <p className="skeleton skeleton-text"></p>
                                    </>
                                )}

                                <SettingsSendCode
                                    selectedNewMethod={selectedNewMethod}
                                    authCodeIsSent={authCodeIsSent}
                                    setAuthCodeIsSent={setAuthCodeIsSent}
                                />
                            </div>

                            <div className="c-account-content__settings-container">
                                {translationsLoaded ? (
                                    <>
                                        <h2>
                                            {t("step")} 2:
                                            <br />
                                            {t("enter-the-code")}
                                        </h2>
                                        <p>
                                            {t(
                                                "enter-the-verification-code-you-received-by-email",
                                            )}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="skeleton skeleton-title"></h2>
                                        <p className="skeleton skeleton-text"></p>
                                    </>
                                )}

                                <SettingsAuthenticationInput
                                    authCodeInput={authCodeInput}
                                    authMethodIsSet={authMethodIsSet}
                                    selectedNewMethod={selectedNewMethod}
                                    setAuthCodeInput={setAuthCodeInput}
                                    setAuthMethodIsSet={setAuthMethodIsSet}
                                />
                            </div>
                        </>
                    )}

                    {selectedMethod.authentication_method ===
                        TwoFactorTypeEnum.sms && (
                        <>
                            <div className="c-account-content__settings-container">
                                {translationsLoaded ? (
                                    <>
                                        <h2>
                                            {t("step")} 1:
                                            <br />
                                            {t(
                                                "receive-your-verification-code",
                                            )}
                                        </h2>
                                        <p>
                                            {t(
                                                "receive-your-verification-code-by-sms",
                                            )}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="skeleton skeleton-title"></h2>
                                        <p className="skeleton skeleton-text"></p>
                                    </>
                                )}

                                <SettingsSendCode
                                    selectedNewMethod={selectedNewMethod}
                                    phoneNumberInput={phoneNumberInput}
                                    authCodeIsSent={authCodeIsSent}
                                    setPhoneNumberInput={setPhoneNumberInput}
                                    setAuthCodeIsSent={setAuthCodeIsSent}
                                />
                            </div>

                            <div className="c-account-content__settings-container">
                                {translationsLoaded ? (
                                    <>
                                        <h2>
                                            {t("step")} 2:
                                            <br />
                                            {t("enter-the-code")}
                                        </h2>
                                        <p>
                                            {t(
                                                "enter-the-verification-code-you-received-by-sms",
                                            )}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="skeleton skeleton-title"></h2>
                                        <p className="skeleton skeleton-text"></p>
                                    </>
                                )}

                                <SettingsAuthenticationInput
                                    authCodeInput={authCodeInput}
                                    authMethodIsSet={authMethodIsSet}
                                    selectedNewMethod={selectedNewMethod}
                                    phoneNumberInput={phoneNumberInput}
                                    setAuthCodeInput={setAuthCodeInput}
                                    setAuthMethodIsSet={setAuthMethodIsSet}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};
